// Groups a flat list of saved-clinic rows by their primary specialty so the
// "My Trusted Medical Clinics" surface can render one banner header per
// specialty with N doctor mini-tiles nested inside.
//
// Aliases must stay in sync with specialtyBanner.js so cohort assignment is
// deterministic — e.g. 'ent_surgery' + 'otolaryngology' + 'audiology' all
// collapse into the 'ent' cohort and share the cochlea banner.

const ALIASES = {
  general_medicine: 'physician',
  internal_medicine: 'physician',
  general_physician: 'physician',
  family_medicine: 'physician',
  general_surgery: 'physician',
  ophthalmology: 'physician',
  ent_surgery: 'ent',
  otolaryngology: 'ent',
  audiology: 'ent',
  cosmetology: 'dermatology',
  aesthetic_medicine: 'dermatology',
  paediatrics: 'pediatrics',
  gynaecology: 'gynecology',
  obs_gyn: 'gynecology',
  obstetrics: 'gynecology',
  orthopaedics: 'orthopedics',
  ortho: 'orthopedics',
  cardiologist: 'cardiology',
  dermatologist: 'dermatology',
};

const LABELS = {
  cardiology: 'Cardiology',
  dermatology: 'Dermatology & Skin',
  ent: 'ENT — Ear, Nose & Throat',
  gastroenterology: 'Gastroenterology',
  physician: 'Internal Medicine / Physician',
  gynecology: 'Gynecology & Obstetrics',
  neurology: 'Neurology',
  neurosurgery: 'Neurosurgery',
  oncology: 'Oncology',
  orthopedics: 'Orthopedics',
  pediatrics: 'Pediatrics',
  psychiatry: 'Psychiatry & Mental Wellness',
  pulmonology: 'Pulmonology',
  urology: 'Urology',
  dentistry: 'Dentistry',
  endocrinology: 'Endocrinology',
  nephrology: 'Nephrology',
  rheumatology: 'Rheumatology',
  plastic_surgery: 'Plastic & Aesthetic Surgery',
  allergy_immunology: 'Allergy & Immunology',
  radiology: 'Radiology & Imaging',
  anesthesiology: 'Anesthesiology',
  physiotherapy: 'Physiotherapy',
  diabetology: 'Diabetology',
  infectious_disease: 'Infectious Disease',
  vascular_surgery: 'Vascular Surgery',
  pain_management: 'Pain Management',
};

const normalise = (slug) => {
  if (!slug) return null;
  const s = String(slug).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return ALIASES[s] || s;
};

// Each clinic row's *primary* specialty drives cohort assignment. If the
// doctor lists ['Cardiology', 'Internal Medicine'] the row joins the
// Cardiology cohort, not the Physician cohort.
const primarySpecialty = (specializations) => {
  const list = Array.isArray(specializations) ? specializations : [];
  for (const raw of list) {
    const n = normalise(raw);
    if (n) return n;
  }
  return 'physician';
};

export function specialtyLabel(slug) {
  return LABELS[slug] || (slug ? slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Doctor');
}

// Returns an array of `{ slug, label, doctors }` sorted alphabetically by
// label so the patient sees a predictable ordering even as their network
// grows.
export default function groupBySpecialty(clinics) {
  const buckets = new Map();
  for (const c of clinics || []) {
    const slug = primarySpecialty(c.specializations);
    if (!buckets.has(slug)) buckets.set(slug, []);
    buckets.get(slug).push(c);
  }
  const groups = Array.from(buckets.entries()).map(([slug, doctors]) => ({
    slug,
    label: specialtyLabel(slug),
    doctors,
  }));
  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
}
